import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { CircularProgress } from '@mui/material'

import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import ForgotPasswordForm from '@hapana/shared/web/components/shared/auth/forgot-password-form/forgot-password-form.component'
import { EMAIL_FIELD_NAME } from '_pages/login/components/forgot-password/forgot-password.constants'
import { extractSubdomain } from '_services/tenant.service'

const zodSchema = z.object({
  [EMAIL_FIELD_NAME]: z.string().email('Invalid email address').min(1, 'Email is required'),
})

const ForgotPassword = ({ defaultEmail = '' }) => {
  const { t } = useTranslation('core')
  const { popDialog } = useDialog()
  const { openToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const formMethods = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: { [EMAIL_FIELD_NAME]: defaultEmail },
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const email = data[EMAIL_FIELD_NAME].toLowerCase()
      const subdomain = extractSubdomain()

      const baseUrl = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:3002'

      const continueUrl = `${window.location.origin}/reset-password`

      const response = await fetch(`${baseUrl}/api/auth/staff/password-reset-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant': subdomain,
        },
        body: JSON.stringify({ email, continueUrl }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send password reset email')
      }

      openToast({
        title: t('modals.forgotPassword.successMessage'),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      })

      popDialog()
    } catch (error) {
      openToast({
        title: t('modals.forgotPassword.errorMessage'),
        description: getErrorMessage(error, t('admin:auth.errors.forgotPasswordFailed')),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isButtonEnabled = isValid && !isLoading

  return (
    <ForgotPasswordForm
      onSubmit={handleSubmit(onSubmit)}
      modalPrimaryAction={{
        label: isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          t('modals.forgotPassword.primaryLabel')
        ),
        action: handleSubmit(onSubmit),
        props: { disabled: !isButtonEnabled },
      }}
      formMethods={formMethods}
    />
  )
}

export default ForgotPassword
